import React, {useRef, useState} from "react";
import {useFormik} from 'formik';
import {InputText} from 'primereact/inputtext';
import {Button} from 'primereact/button';
import {classNames} from 'primereact/utils';
import {MainLayout} from "../../components/MainLayout";
import {Toast} from 'primereact/toast';
import Loading from "../../components/shared/Loading";
import {generateOTP, verifyOTP ,resetPassword} from "../../api/methods";
import "./ForgotPassword.styles.scss";
import OTPModal from "./Component/OTPModal"
import ResetPasswordModal from "./Component/ResetPasswordModal"
import {useNavigate} from "react-router-dom";

const ForgotPassword = () => {
    const toast = useRef(null);
    const [loader, setLoader] = useState(false);
    const [modalIsOpen,setModalIsOpen] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [userName, setUserName] = useState("");
    const [mobileNumber, setMobileNumber] = useState("");
    const [showResetModal, setShowResetModal] = useState(false);
    const [verifiedOtp, setVerifiedOtp] = useState("");
    const [otpTimerSeconds, setOtpTimerSeconds] = useState(0);
    // const [password, setPassword] = useState("");
    const navigate = useNavigate();
    const handleOTPVerify = async (enteredOtp) => {
        setLoader(true);
              try {
            await verifyOTP({
                otp: enteredOtp,
                userName: userName
            });
            setVerifiedOtp(enteredOtp);
            setModalIsOpen(false);
            setShowResetModal(true);

            // toast.current.show({
            //     severity: 'success',
            //     summary: 'OTP Verified',
            //     detail: 'OTP verified successfully.',
            //     life: 3000
            // });


        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Invalid OTP',
                detail: 'Invalid OTP. Please try again.',
                life: 5000
            });
        } finally {
            setLoader(false);
            setModalIsOpen(false);
        }
    };

    const handleOtpResend = () => {
        if (!userName) return;
        setModalIsOpen(false);
        setLoader(true);
        generateOTP({ userName, mobileNumber })
            .then((response) => {
                setPhoneNumber(response.mobile);
                setOtpTimerSeconds((response.otpExpiryMinutes ?? 3) * 60);
                setModalIsOpen(true);
            })
            .catch((error) => {
                toast.current.show({
                    severity: 'error',
                    summary: 'Resend Failed',
                    detail: 'Failed to resend OTP. Please try again.',
                    life: 5000
                });
            })
            .finally(() => setLoader(false));
    };

    const handleOtpClose = () => {
        setModalIsOpen(false);
        setPhoneNumber('');
        setOtpTimerSeconds(0);
    };

    const goToLogin = () => {
        navigate("/login");
    }

    const handleResetPassword = async (password) => {
        if (!password) return;

        setLoader(true);
        try {
            await resetPassword({
                userName,
                password,
                otp: verifiedOtp
            });

            // toast.current.show({
            //     severity: 'success',
            //     summary: 'Password Reset',
            //     detail: 'Your password has been reset successfully.',
            //     life: 3000
            // });

            setShowResetModal(false);

            navigate("/login", {
                state: { showToast: true, toastMessage: "Password reset successfully!" },
                replace: true
            });

        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Reset Failed',
                detail: 'Failed to reset password.',
                life: 5000
            });
        } finally {
            setLoader(false);
        }
    };

    const formik = useFormik({
        initialValues: {
            userName: '',
            mobileNumber: ''
        }, validate: (data) => {
            let errors = {};

            if (!data.userName) {
                errors.userName = 'Username is required.';
            }

            if (!data.mobileNumber) {
                errors.mobileNumber = 'Mobile number is required.';
            } else if (!/^[0-9]{10}$/.test(data.mobileNumber)) {
                errors.mobileNumber = 'Mobile number must be exactly 10 digits.';
            }

            return errors;
        }, onSubmit: (data) => {
            setUserName(data.userName);
            setMobileNumber(data.mobileNumber);
            setLoader(true);
            generateOTP(data)
                .then((response) => {
                    setPhoneNumber(response.mobile);
                    setOtpTimerSeconds((response.otpExpiryMinutes ?? 3) * 60);
                    setModalIsOpen(true);
                })
                .catch((error) => {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Reset Password Failed',
                        detail: 'Invalid credentials. Please check your username and mobile number.',
                        life: 5000
                    });
                    console.error("Reset Password:", error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
    };

    return (
        <MainLayout hideHeader={true}>
            <Toast ref={toast} />
            {loader && <Loading />}

            <OTPModal
                isOpen={modalIsOpen}
                phoneNumber={phoneNumber}
                onClose={handleOtpClose}
                onVerify={handleOTPVerify}
                onResend={handleOtpResend}
                initialTimer={otpTimerSeconds}
            />
            <ResetPasswordModal
                isOpen={showResetModal}
                onClose={() => setShowResetModal(false)}
                onSubmit={handleResetPassword}
            />


            <div className="login-container">
                {/* Left Panel - Login Form */}
                <div className="login-form-panel">
                    <div className="form-header">
                        <h2>Forgot Password ? </h2>
                        <p>Please enter your username to continue</p>
                    </div>

                    <form onSubmit={formik.handleSubmit} className="login-form">
                        <div className="field">
                            <label
                                htmlFor="userName"
                                className={classNames({ 'p-error': isFormFieldValid('userName') })}
                            >
                                Username
                            </label>
                            <div className="input-group">
                                <i className="pi pi-user input-icon"></i>
                                <InputText
                                    id="userName"
                                    name="userName"
                                    value={formik.values.userName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Enter your username"
                                    className={classNames({ 'p-invalid': isFormFieldValid('userName') })}
                                />
                            </div>
                            {getFormErrorMessage('userName')}
                        </div>

                        <div className="field">
                            <label
                                htmlFor="mobileNumber"
                                className={classNames({ 'p-error': isFormFieldValid('mobileNumber') })}
                            >
                                Mobile Number
                            </label>
                            <div className="input-group">
                                <i className="pi pi-phone input-icon"></i>
                                <InputText
                                    id="mobileNumber"
                                    name="mobileNumber"
                                    value={formik.values.mobileNumber}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="07xxxxxxxx"
                                    maxLength={10}
                                    className={classNames({ 'p-invalid': isFormFieldValid('mobileNumber') })}
                                />
                            </div>
                            {getFormErrorMessage('mobileNumber')}
                        </div>

                        <Button
                            type="submit"
                            label={loader ? "Submitting..." : "Submit"}
                            icon="pi pi-sign-in"
                            iconPos="right"
                            className="login-button"
                            disabled={loader}
                        />

                    </form>
                    <div className="forgot-password-link">
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={!loader ? goToLogin : undefined}
                        onKeyDown={(e) =>
                            !loader && e.key === "Enter" && goToLogin()
                        }
                        style={{
                            pointerEvents: loader ? "none" : "auto",
                            opacity: loader ? 0.6 : 1,
                            cursor: loader ? "not-allowed" : "pointer"
                        }}
                    >
        Back to login
    </span>
                    </div>

                    <div className="form-footer">
                        <div className="divider">
                            <span>Need assistance?</span>
                        </div>
                        <div className="support-info">
                            For technical support, contact{' '}
                            <a href="https://b-tek.dev/" target="_blank" rel="noopener noreferrer">
                                B-TEK Support Team
                            </a>
                        </div>
                    </div>
                </div>

                {/* Right Panel - Branding */}
                <div className="login-brand-panel">
                    <div className="decoration decoration-1"></div>
                    <div className="decoration decoration-2"></div>

                    <div className="brand-content">
                        <img src="/logo192.png" alt="Logo" />
                        <h1>Jofotara Tax Administration Dashboard</h1>
                        <p>
                            Modern tax administration solution that digitizes revenue collection processes, enhances taxpayer services, and provides real-time insights for informed policy decisions.
                        </p>

                        <ul className="feature-list">
                            <li>Tax Collection Management</li>
                            <li>Taxpayer Registration & Monitoring</li>
                            <li>Compliance Tracking & Enforcement</li>
                            <li>Revenue Analytics & Reporting</li>
                            <li>Audit Management System</li>
                        </ul>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default React.memo(ForgotPassword);
